import React, { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, ArrowLeft, ClipboardCheck, Play, Check, Upload, AlertTriangle,
  TrendingDown, TrendingUp, RefreshCcw, Search, Calendar
} from 'lucide-react';
import { BO_BASE } from '@/utils/getBasePath';

// Radix Select refuses an item whose value is "": "none" is this sentinel, stored as ''.
const NONE = '__none__';
const bo = (route = '') => route ? `${BO_BASE}/${route}` : (BO_BASE || '/');

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InventoryCountingPage() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('sessions');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [createForm, setCreateForm] = useState({
    name: '',
    count_type: 'FULL',
    location_id: '',
    category_id: '',
    scheduled_date: '',
    notes: ''
  });

  const { token = localStorage.getItem('backoffice_token') } = useOutletContext() || {};
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useLatestCallback(async () => {
    setLoading(true);
    try {
      const [sessionsRes, locsRes, catsRes] = await Promise.all([
        fetch(`${API_URL}/api/inv/count-sessions`, { headers }),
        fetch(`${API_URL}/api/inv/locations`, { headers }),
        fetch(`${API_URL}/api/inv/categories`, { headers })
      ]);
      
      if (sessionsRes.ok) setSessions(await sessionsRes.json());
      if (locsRes.ok) setLocations(await locsRes.json());
      if (catsRes.ok) setCategories(await catsRes.json());
    } catch (err) {
      console.error('Error fetching data:', err);
    }
    setLoading(false);
  });

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createSession = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/inv/count-sessions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...createForm,
          location_id: createForm.location_id || null,
          category_id: createForm.category_id || null,
          scheduled_date: createForm.scheduled_date || null
        })
      });
      
      if (res.ok) {
        setShowCreateDialog(false);
        setCreateForm({ name: '', count_type: 'FULL', location_id: '', category_id: '', scheduled_date: '', notes: '' });
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error creating count session');
      }
    } catch (err) {
      console.error('Error creating session:', err);
    }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/inv/count-sessions/${sessionId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSelectedSession(data);
        setActiveTab('counting');
      }
    } catch (err) {
      console.error('Error loading session:', err);
    }
  };

  const startSession = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/inv/count-sessions/${sessionId}/start`, {
        method: 'PATCH',
        headers
      });
      if (res.ok) {
        loadSession(sessionId);
        fetchData();
      }
    } catch (err) {
      console.error('Error starting session:', err);
    }
  };

  const updateEntry = async (entryId, countedQty, notes = '') => {
    try {
      const res = await fetch(`${API_URL}/api/inv/count-entries/${entryId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ counted_qty: parseFloat(countedQty), notes })
      });
      
      if (res.ok) {
        // Refresh session
        loadSession(selectedSession.id);
      }
    } catch (err) {
      console.error('Error updating entry:', err);
    }
  };

  const completeSession = async () => {
    if (!window.confirm('Complete this count session? All items must be counted.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/inv/count-sessions/${selectedSession.id}/complete`, {
        method: 'PATCH',
        headers
      });
      
      if (res.ok) {
        loadSession(selectedSession.id);
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error completing session');
      }
    } catch (err) {
      console.error('Error completing session:', err);
    }
  };

  const postAdjustments = async () => {
    if (!window.confirm('Post all adjustments? This will update inventory quantities and cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/inv/count-sessions/${selectedSession.id}/post`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        const result = await res.json();
        alert(`Posted ${result.adjustments_made} adjustments successfully!`);
        loadSession(selectedSession.id);
        fetchData();
      } else {
        const err = await res.json();
        alert(err.detail || 'Error posting adjustments');
      }
    } catch (err) {
      console.error('Error posting adjustments:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-600',
      'IN_PROGRESS': 'bg-blue-600',
      'COMPLETED': 'bg-yellow-600',
      'POSTED': 'bg-green-600',
      'CANCELED': 'bg-red-600'
    };
    return colors[status] || 'bg-gray-600';
  };

  const getVarianceColor = (value) => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const filteredEntries = selectedSession?.entries?.filter(e => {
    if (!searchTerm) return true;
    return e.item_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (e.item_sku && e.item_sku.toLowerCase().includes(searchTerm.toLowerCase()));
  }) || [];

  return (
    <div className="p-6 space-y-6 dark rounded-2xl bg-slate-950 text-slate-100" data-testid="inventory-counting-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <a href={bo('inventory')} className="p-2 hover:bg-gray-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-gray-400" />
        </a>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-white">Inventory Counting</h1>
          <p className="text-gray-400">Physical counts and variance tracking</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-800/50 border border-gray-700">
          <TabsTrigger value="sessions" className="data-[state=active]:bg-blue-600">
            Count Sessions
          </TabsTrigger>
          <TabsTrigger value="counting" className="data-[state=active]:bg-blue-600" disabled={!selectedSession}>
            {selectedSession ? `Counting: ${selectedSession.session_number}` : 'Counting'}
          </TabsTrigger>
        </TabsList>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="mt-4">
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-white">Count Sessions</CardTitle>
                  <CardDescription>Create and manage physical inventory counts</CardDescription>
                </div>
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-count-btn">
                      <Plus className="w-4 h-4 mr-2" /> New Count Session
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gray-900 border-gray-700 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-white">Create Count Session</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={createSession} className="space-y-4">
                      <div>
                        <Label className="text-gray-300">Name (optional)</Label>
                        <Input
                          value={createForm.name}
                          onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                          className="bg-gray-800 border-gray-700"
                          placeholder="e.g., Weekly Count, Month End"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Count Type</Label>
                        <Select
                          value={createForm.count_type}
                          onValueChange={(v) => setCreateForm({ ...createForm, count_type: v })}
                        >
                          <SelectTrigger className="bg-gray-800 border-gray-700">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            <SelectItem value="FULL">Full Count (All Items)</SelectItem>
                            <SelectItem value="PARTIAL">Partial (Category/Location)</SelectItem>
                            <SelectItem value="CYCLE">Cycle Count</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Location (optional - all if empty)</Label>
                        <Select
                          value={createForm.location_id || NONE}
                          onValueChange={(v) => setCreateForm({ ...createForm, location_id: v === NONE ? '' : v })}
                        >
                          <SelectTrigger className="bg-gray-800 border-gray-700">
                            <SelectValue placeholder="All locations" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            <SelectItem value={NONE}>All Locations</SelectItem>
                            {locations.map(l => (
                              <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Category (optional - all if empty)</Label>
                        <Select
                          value={createForm.category_id || NONE}
                          onValueChange={(v) => setCreateForm({ ...createForm, category_id: v === NONE ? '' : v })}
                        >
                          <SelectTrigger className="bg-gray-800 border-gray-700">
                            <SelectValue placeholder="All categories" />
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            <SelectItem value={NONE}>All Categories</SelectItem>
                            {categories.map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-300">Scheduled Date (optional)</Label>
                        <Input
                          type="date"
                          value={createForm.scheduled_date}
                          onChange={(e) => setCreateForm({ ...createForm, scheduled_date: e.target.value })}
                          className="bg-gray-800 border-gray-700"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">Create Session</Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr>
                      <th className="text-left p-4 text-gray-400 font-medium">Session #</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Name/Type</th>
                      <th className="text-left p-4 text-gray-400 font-medium">Location</th>
                      <th className="text-center p-4 text-gray-400 font-medium">Progress</th>
                      <th className="text-right p-4 text-gray-400 font-medium">Variance</th>
                      <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                      <th className="text-center p-4 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map(session => (
                      <tr key={session.id} className="border-t border-gray-700 hover:bg-gray-800/30">
                        <td className="p-4">
                          <p className="font-medium text-white">{session.session_number}</p>
                          <p className="text-sm text-gray-400">{session.created_by_name}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-white">{session.name || session.count_type}</p>
                          <p className="text-sm text-gray-400">{session.count_type}</p>
                        </td>
                        <td className="p-4 text-gray-400">
                          {session.location_name || 'All'}
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-white">{session.items_counted}</span>
                            <span className="text-gray-400">/</span>
                            <span className="text-gray-400">{session.total_items}</span>
                          </div>
                          <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
                            <div 
                              className="bg-blue-600 h-1.5 rounded-full" 
                              style={{ width: `${(session.items_counted / session.total_items) * 100}%` }}
                            />
                          </div>
                        </td>
                        <td className={`p-4 text-right font-medium ${getVarianceColor(session.total_variance_value)}`}>
                          {session.total_variance_value >= 0 ? '+' : ''}${session.total_variance_value.toFixed(2)}
                          {session.items_with_variance > 0 && (
                            <p className="text-xs text-gray-400">{session.items_with_variance} items</p>
                          )}
                        </td>
                        <td className="p-4 text-center">
                          <Badge className={getStatusColor(session.status)}>{session.status}</Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex justify-center gap-2">
                            {session.status === 'DRAFT' && (
                              <Button size="sm" onClick={() => startSession(session.id)}>
                                <Play className="w-4 h-4 mr-1" /> Start
                              </Button>
                            )}
                            {['DRAFT', 'IN_PROGRESS', 'COMPLETED', 'POSTED'].includes(session.status) && (
                              <Button size="sm" variant="outline" onClick={() => loadSession(session.id)}>
                                <ClipboardCheck className="w-4 h-4 mr-1" /> 
                                {session.status === 'POSTED' ? 'View' : 'Count'}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {sessions.length === 0 && (
                      <tr>
                        <td colSpan="7" className="p-8 text-center text-gray-400">
                          {loading ? 'Loading...' : 'No count sessions found. Create your first count!'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Counting Tab */}
        <TabsContent value="counting" className="mt-4">
          {selectedSession && (
            <div className="space-y-4">
              {/* Session Info Card */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">{selectedSession.session_number}</h3>
                        <p className="text-sm text-gray-400">{selectedSession.name || selectedSession.count_type}</p>
                      </div>
                      <Badge className={getStatusColor(selectedSession.status)}>{selectedSession.status}</Badge>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Progress</p>
                        <p className="text-white font-medium">
                          {selectedSession.items_counted} / {selectedSession.total_items}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-400">Total Variance</p>
                        <p className={`font-medium ${getVarianceColor(selectedSession.total_variance_value)}`}>
                          {selectedSession.total_variance_value >= 0 ? '+' : ''}
                          ${selectedSession.total_variance_value?.toFixed(2)}
                        </p>
                      </div>
                      {selectedSession.status === 'IN_PROGRESS' && (
                        <Button onClick={completeSession} className="bg-yellow-600 hover:bg-yellow-500">
                          <Check className="w-4 h-4 mr-2" /> Complete Count
                        </Button>
                      )}
                      {selectedSession.status === 'COMPLETED' && (
                        <Button onClick={postAdjustments} className="bg-green-600 hover:bg-green-500">
                          <Upload className="w-4 h-4 mr-2" /> Post Adjustments
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Search */}
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-700"
                />
              </div>

              {/* Count Entries */}
              <Card className="bg-gray-900/50 border-gray-700">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-800/50">
                        <tr>
                          <th className="text-left p-4 text-gray-400 font-medium">Item</th>
                          <th className="text-left p-4 text-gray-400 font-medium">Location</th>
                          <th className="text-right p-4 text-gray-400 font-medium">System Qty</th>
                          <th className="text-center p-4 text-gray-400 font-medium">Counted Qty</th>
                          <th className="text-right p-4 text-gray-400 font-medium">Variance</th>
                          <th className="text-right p-4 text-gray-400 font-medium">Variance $</th>
                          <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEntries.map(entry => (
                          <CountEntryRow 
                            key={entry.id} 
                            entry={entry} 
                            onUpdate={updateEntry}
                            disabled={!['DRAFT', 'IN_PROGRESS'].includes(selectedSession.status)}
                          />
                        ))}
                        {filteredEntries.length === 0 && (
                          <tr>
                            <td colSpan="7" className="p-8 text-center text-gray-400">
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Count Entry Row Component
function CountEntryRow({ entry, onUpdate, disabled }) {
  const [countValue, setCountValue] = useState(entry.counted_qty ?? '');
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    if (countValue !== '' && !isNaN(parseFloat(countValue))) {
      onUpdate(entry.id, countValue);
      setIsEditing(false);
    }
  };

  const getVarianceColor = (value) => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  return (
    <tr className={`border-t border-gray-700 hover:bg-gray-800/30 ${entry.is_counted ? '' : 'bg-yellow-900/10'}`}>
      <td className="p-4">
        <p className="text-white font-medium">{entry.item_name}</p>
        {entry.item_sku && <p className="text-sm text-gray-400">{entry.item_sku}</p>}
      </td>
      <td className="p-4 text-gray-400">{entry.location_name || '-'}</td>
      <td className="p-4 text-right text-white">{entry.system_qty.toFixed(2)}</td>
      <td className="p-4">
        <div className="flex justify-center">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.01"
                value={countValue}
                onChange={(e) => setCountValue(e.target.value)}
                className="w-24 bg-gray-800 border-gray-700 text-center"
                autoFocus
                onKeyPress={(e) => e.key === 'Enter' && handleSave()}
              />
              <Button size="sm" onClick={handleSave}>
                <Check className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => !disabled && setIsEditing(true)}
              disabled={disabled}
              className={entry.is_counted ? 'bg-gray-800' : 'bg-yellow-900/30 border-yellow-600'}
            >
              {entry.is_counted ? entry.counted_qty?.toFixed(2) : 'Enter Count'}
            </Button>
          )}
        </div>
      </td>
      <td className={`p-4 text-right font-medium ${getVarianceColor(entry.variance_qty)}`}>
        {entry.is_counted ? (
          <>
            {entry.variance_qty >= 0 ? '+' : ''}{entry.variance_qty.toFixed(2)}
            {entry.variance_qty !== 0 && (
              entry.variance_qty > 0 
                ? <TrendingUp className="w-4 h-4 inline ml-1" />
                : <TrendingDown className="w-4 h-4 inline ml-1" />
            )}
          </>
        ) : '-'}
      </td>
      <td className={`p-4 text-right ${getVarianceColor(entry.variance_value)}`}>
        {entry.is_counted ? (
          `${entry.variance_value >= 0 ? '+' : ''}$${entry.variance_value.toFixed(2)}`
        ) : '-'}
      </td>
      <td className="p-4 text-center">
        {entry.is_adjusted ? (
          <Badge className="bg-green-600">Adjusted</Badge>
        ) : entry.is_counted ? (
          entry.variance_qty !== 0 ? (
            <Badge className="bg-yellow-600">Variance</Badge>
          ) : (
            <Badge className="bg-blue-600">OK</Badge>
          )
        ) : (
          <Badge variant="secondary">Pending</Badge>
        )}
      </td>
    </tr>
  );
}
